import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Card, Callout, Spinner, TextField, Modal, Badge, Select } from "@/design/components";
import { ApiError } from "@/api/client";
import {
  listIntegrations,
  createIntegration,
  deleteIntegration,
  testConnection,
  isRelayUnavailable,
  type Integration,
  type IntegrationStatus,
  type CreateIntegrationInput,
  type TestConnectionResult,
} from "@/api/integrations";
import { listChannels } from "@/api/channels";
import { PROVIDERS, type ProviderId, type ProviderMeta } from "./providers";
import styles from "./IntegrationsScreen.module.css";

const apiMessage = (err: unknown, fallback: string): string =>
  err instanceof ApiError ? err.message : fallback;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// Provider id → display label / credential fields, sourced from the registry so a
// new provider is a data change (providers.ts), not new UI here.
const PROVIDER_BY_ID: Record<ProviderId, ProviderMeta> = Object.fromEntries(
  PROVIDERS.map((p) => [p.id, p]),
) as Record<ProviderId, ProviderMeta>;
const PROVIDER_OPTIONS = PROVIDERS.map((p) => ({ value: p.id, label: p.label }));

// Status → Badge tone + label. active=connected, invalid=creds rejected,
// unverified=not yet checked (neutral).
type BadgeTone = "success" | "danger" | "neutral" | "warning";
const STATUS_TONE: Record<IntegrationStatus, BadgeTone> = {
  active: "success",
  invalid: "danger",
  unverified: "neutral",
};
const STATUS_LABEL: Record<IntegrationStatus, string> = {
  active: "Active",
  invalid: "Invalid credentials",
  unverified: "Unverified",
};

// --- Preview gate (MT surfaces) -------------------------------------------------
// The connect + "Test connection" flow is live and functional (creds are validated
// against the provider and stored encrypted), but the runtime that actually RELAYS
// messages for Telegram/Discord/WhatsApp isn't wired yet. Until it is, show a Preview
// banner + an inline note at the point of action so customers aren't misled into
// expecting two-way delivery. Flip to true (or remove the banner) when delivery ships.
// The master vault is silent on product/UX gating → grounded in the in-repo Callout
// pattern (see the "aren't enabled yet" warning below).
const DELIVERY_LIVE = false;

/**
 * Status → badge, gated on delivery actually being wired.
 *
 * `active` is set when the provider API accepted the credentials (Telegram getMe,
 * Discord /users/@me, WhatsApp token check) — it means "this token authenticates",
 * NOT "messages flow". The control plane cannot observe whether an adapter process
 * is running, so while DELIVERY_LIVE is false it must not render a success badge
 * that customers read as "connected and working".
 *
 * 2026-09-10: it did, and a customer believed it — he went debugging Telegram's
 * webhook API for a bot nothing was polling. See
 * docs/analysis/2026-09-10-surface-integrations-delivery-audit.md (cc-cloud-bridge).
 */
function statusBadge(status: IntegrationStatus): { tone: BadgeTone; label: string } {
  if (status === "active" && !DELIVERY_LIVE) {
    return { tone: "warning", label: "Validated — not yet delivering" };
  }
  return { tone: STATUS_TONE[status], label: STATUS_LABEL[status] };
}

function StatusBadge({ status }: { status: IntegrationStatus }) {
  const { tone, label } = statusBadge(status);
  return <Badge tone={tone}>{label}</Badge>;
}

/**
 * Integrations — the admin surface for BYO messaging channels: bind a customer's
 * own Telegram/Discord/WhatsApp bot to a Cogent channel so messages relay both
 * ways. Mirrors the Channels screen: list the org's integrations, connect one
 * (with a live "Test connection" check), and disconnect one.
 */
export function IntegrationsScreen() {
  const qc = useQueryClient();
  const integrationsQuery = useQuery({
    queryKey: ["integrations"],
    queryFn: ({ signal }) => listIntegrations(signal),
  });
  // The channel <Select> reuses the Channels data source (shared cache key). If the
  // relay link is off this errors softly; we degrade the picker to empty options
  // rather than blocking the whole screen — the integrations query drives the gate.
  const channelsQuery = useQuery({ queryKey: ["channels"], queryFn: ({ signal }) => listChannels(signal) });
  const channelOptions = useMemo(
    () => (channelsQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [channelsQuery.data],
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Integration | null>(null);

  const create = useMutation({
    mutationFn: (input: CreateIntegrationInput) => createIntegration(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations"] });
      setCreateOpen(false);
    },
  });

  // Test connection: validate creds without persisting; the result renders inline.
  const test = useMutation({
    mutationFn: (input: { provider: ProviderId; credentials: Record<string, string> }) => testConnection(input),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteIntegration(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations"] });
      setToDelete(null);
    },
  });

  function openCreate() {
    create.reset();
    test.reset();
    setCreateOpen(true);
  }
  function closeCreate() {
    if (create.isPending) return;
    setCreateOpen(false);
  }
  function openDelete(i: Integration) {
    // Clear any prior failure so a previous row's error can't leak into this confirm.
    remove.reset();
    setToDelete(i);
  }
  function closeDelete() {
    if (remove.isPending) return;
    remove.reset();
    setToDelete(null);
  }

  const { data: integrations, isPending, isError, error } = integrationsQuery;
  const unavailable = isError && isRelayUnavailable(error);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Control plane</p>
          <h1 className={styles.h1}>Integrations</h1>
        </div>
        {!isPending && !isError && (
          <div className={styles.headActions}>
            <Button onClick={openCreate} iconBefore={<PlusIcon />}>
              New integration
            </Button>
          </div>
        )}
      </header>

      {!DELIVERY_LIVE && (
        <Callout tone="warning" title="Preview — message delivery is rolling out">
          You can connect and validate your Telegram, Discord, or WhatsApp bot here now. Two-way
          message relay is still rolling out, so a connected bot won't deliver messages yet — we'll
          switch your integrations on automatically once it's live.
        </Callout>
      )}

      {isPending && (
        <div className={styles.loading}>
          <Spinner size={22} color="var(--accent)" /> <span>Loading integrations…</span>
        </div>
      )}

      {unavailable && (
        <Callout tone="warning" title="Integrations aren't enabled yet">
          The control plane isn't linked to a Cogent relay, so integrations can't be managed from here
          yet. Once the relay connection is configured, your integrations will appear here.
        </Callout>
      )}

      {isError && !unavailable && (
        <Callout tone="danger" title="Couldn't load integrations">
          {apiMessage(error, "Please try again shortly.")}
        </Callout>
      )}

      {integrations && integrations.length === 0 && (
        <Card className={styles.empty}>
          <h2 className={styles.emptyTitle}>No integrations yet</h2>
          <p className={styles.emptyBlurb}>
            Connect your own Telegram, Discord, or WhatsApp bot to a Cogent channel, and messages relay
            both ways. You keep ownership of the bot — Cogent just bridges it.
          </p>
          <Button onClick={openCreate} iconBefore={<PlusIcon />}>
            Connect your first integration
          </Button>
        </Card>
      )}

      {integrations && integrations.length > 0 && (
        <ul className={styles.list}>
          {integrations.map((i) => (
            <li key={i.id}>
              <Card className={styles.row}>
                <div className={styles.rowMain}>
                  <h2 className={styles.name}>{`${PROVIDER_BY_ID[i.provider]?.label ?? i.provider} → ${i.channelName}`}</h2>
                  <div className={styles.status}>
                    <StatusBadge status={i.status} />
                    {i.credHint && <span className={styles.hint}>{i.credHint}</span>}
                  </div>
                  <p className={styles.meta}>
                    Connected {formatDate(i.createdAt)}
                    {i.lastVerifiedAt ? ` · Last verified ${formatDate(i.lastVerifiedAt)}` : ""}
                  </p>
                </div>
                <div className={styles.rowActions}>
                  <Button variant="danger" size="sm" onClick={() => openDelete(i)}>
                    Disconnect
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {/* Connect integration */}
      <Modal open={createOpen} onClose={closeCreate} title="New integration">
        <CreateIntegrationForm
          channelOptions={channelOptions}
          pending={create.isPending}
          error={create.isError ? apiMessage(create.error, "Couldn't create the integration.") : null}
          testResult={test.data ?? null}
          testPending={test.isPending}
          testError={test.isError ? apiMessage(test.error, "Couldn't test the connection.") : null}
          onTest={(provider, credentials) => test.mutate({ provider, credentials })}
          onSubmit={(values) => create.mutate(values)}
        />
      </Modal>

      {/* Disconnect confirm */}
      <Modal
        open={toDelete !== null}
        onClose={closeDelete}
        title="Disconnect integration?"
        footer={
          <>
            <Button variant="ghost" onClick={closeDelete} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => toDelete && remove.mutate(toDelete.id)}
            >
              Disconnect
            </Button>
          </>
        }
      >
        <p>
          Disconnect <strong>{toDelete ? (PROVIDER_BY_ID[toDelete.provider]?.label ?? toDelete.provider) : ""}</strong>{" "}
          from <strong>{toDelete?.channelName}</strong>? The bot stops relaying to and from this Cogent
          channel. This can't be undone — you can reconnect it later with the same credentials.
        </p>
        {remove.isError && (
          <Callout tone="danger">{apiMessage(remove.error, "Couldn't disconnect the integration.")}</Callout>
        )}
      </Modal>
    </div>
  );
}

// --- Connect form (RHF + Zod). Provider-generic: the selected provider's fields
//     from the registry render dynamically. Lives inside the Modal, so it unmounts
//     on close → fresh state on each open. ---
// The client schema mirrors the server contract (provider + channel + secret + the
// selected provider's required credential fields). Credential validation is
// provider-dependent, so a superRefine requires each field the chosen provider
// declares (providers.ts) — before submit, with a clear per-field message.
const createIntegrationSchema = z
  .object({
    provider: z.enum(["telegram", "discord", "whatsapp"]),
    channelId: z.string().min(1, "Select a channel"),
    channelSecret: z.string().min(1, "Your channel's join secret is required"),
    orgId: z.string().optional(),
    credentials: z.record(z.string(), z.string()),
  })
  .superRefine((val, ctx) => {
    const meta = PROVIDER_BY_ID[val.provider];
    for (const f of meta?.fields ?? []) {
      if (!val.credentials?.[f.name]?.trim()) {
        ctx.addIssue({ code: "custom", path: ["credentials", f.name], message: `${f.label} is required` });
      }
    }
  });
type CreateIntegrationValues = z.infer<typeof createIntegrationSchema>;

/** Keep only the selected provider's credential fields (trimmed) — drops any stale
 *  values left in form state from a previously-chosen provider. */
function pickCredentials(meta: ProviderMeta | undefined, creds: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of meta?.fields ?? []) out[f.name] = (creds?.[f.name] ?? "").trim();
  return out;
}

function CreateIntegrationForm({
  channelOptions,
  pending,
  error,
  testResult,
  testPending,
  testError,
  onTest,
  onSubmit,
}: {
  channelOptions: { value: string; label: string }[];
  pending: boolean;
  error: string | null;
  testResult: TestConnectionResult | null;
  testPending: boolean;
  testError: string | null;
  onTest: (provider: ProviderId, credentials: Record<string, string>) => void;
  onSubmit: (values: CreateIntegrationInput) => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<CreateIntegrationValues>({
    resolver: zodResolver(createIntegrationSchema),
    defaultValues: { provider: "telegram", channelId: "", channelSecret: "", orgId: "", credentials: {} },
  });

  const provider = watch("provider");
  const meta = PROVIDER_BY_ID[provider] ?? PROVIDERS[0]!;
  const credErrors = errors.credentials as Record<string, { message?: string }> | undefined;

  function submit(values: CreateIntegrationValues) {
    onSubmit({
      provider: values.provider,
      channelId: values.channelId,
      channelSecret: values.channelSecret,
      orgId: values.orgId?.trim() ? values.orgId.trim() : undefined,
      credentials: pickCredentials(PROVIDER_BY_ID[values.provider], values.credentials),
    });
  }

  function handleTest() {
    const values = getValues();
    onTest(values.provider, pickCredentials(PROVIDER_BY_ID[values.provider], values.credentials));
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(submit)} noValidate>
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <Select label="Provider" options={PROVIDER_OPTIONS} error={errors.provider?.message} {...register("provider")} />
      <Select
        label="Cogent channel"
        options={[{ value: "", label: "Choose a channel…" }, ...channelOptions]}
        hint="The Cogent channel this bot relays to."
        required
        error={errors.channelId?.message}
        {...register("channelId")}
      />
      <TextField
        label="Channel secret"
        type="password"
        autoComplete="off"
        hint="your channel's join password"
        required
        error={errors.channelSecret?.message}
        {...register("channelSecret")}
      />
      <TextField
        label="Org_ID"
        type="password"
        autoComplete="off"
        hint="for Team channels; leave blank for free"
        error={errors.orgId?.message}
        {...register("orgId")}
      />
      {meta.fields.map((f) => (
        <TextField
          key={`${meta.id}.${f.name}`}
          label={f.label}
          type={f.secret ? "password" : "text"}
          autoComplete="off"
          hint={f.hint}
          required
          error={credErrors?.[f.name]?.message}
          {...register(`credentials.${f.name}`)}
        />
      ))}
      <p className={styles.docsLine}>
        <a href={meta.docsUrl} target="_blank" rel="noreferrer">
          Where do I find these? ({meta.label} docs)
        </a>
      </p>
      {testResult && (
        <div role="status">
          <Callout tone={testResult.ok ? "success" : "danger"}>
            {testResult.ok
              ? testResult.detail ?? "Connection looks good — the credentials work."
              : testResult.detail ?? "Those credentials didn't work. Double-check them and try again."}
          </Callout>
        </div>
      )}
      {testError && (
        <div role="alert">
          <Callout tone="danger">{testError}</Callout>
        </div>
      )}
      {!DELIVERY_LIVE && (
        <div role="note">
          <Callout tone="warning">
            Preview: you can save and validate credentials now, but this bot won't relay messages
            until delivery ships (rolling out shortly).
          </Callout>
        </div>
      )}
      <div className={styles.formActions}>
        <Button type="button" variant="secondary" onClick={handleTest} loading={testPending}>
          Test connection
        </Button>
        <Button type="submit" loading={pending}>
          Connect
        </Button>
      </div>
    </form>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
